import React from 'react';
import { ImageOff, Lock } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface PicturePlaceholderProps {
    /** What is not there and why — "Private — not shared by the shopper", "No room picture shared". */
    label: string;
    /** A second, quieter line (large size only). */
    hint?: string;
    /** `private`: a picture exists but is the shopper's (lock); `none`: there is no picture to show. */
    reason?: 'private' | 'none';
    /** `xs` a table thumbnail (label as a tooltip), `md` a card, `lg` the picture area of a drawer. */
    size?: 'xs' | 'md' | 'lg';
    className?: string;
    testId?: string;
}

/**
 * Where a picture of a shopper's room would be, when the shop may not see it
 * or there is none: a calm, neutral frame that says so — never an empty grey
 * box that looks like a broken image.
 *
 * One look for the saved rooms page, the lead file and anywhere else a room
 * picture is held back.
 */
export const PicturePlaceholder: React.FC<PicturePlaceholderProps> = ({ label, hint, reason = 'private', size = 'md', className, testId }) => {
    const Icon = reason === 'private' ? Lock : ImageOff;
    if (size === 'xs') {
        return (
            <span
                className={cn('flex h-10 w-14 shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground', className)}
                title={label}
                role="img"
                aria-label={label}
                data-testid={testId}
            >
                <Icon className="size-3.5" aria-hidden />
            </span>
        );
    }
    return (
        <div
            className={cn(
                'flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border bg-muted/40 px-4 text-center text-muted-foreground',
                size === 'lg' ? 'aspect-[4/3] max-h-72' : 'aspect-[16/9]',
                className,
            )}
            data-testid={testId}
        >
            <Icon className="size-5" aria-hidden />
            <p className="text-sm text-foreground">{label}</p>
            {hint && size === 'lg' && <p className="max-w-sm text-xs">{hint}</p>}
        </div>
    );
};
